import React from "react";
import moment from "moment";
import { CalendarDays, ChevronRight, IndianRupee, MapPin, Search, X } from "lucide-react";
import Cross from "sushiweb/Icons/all/Cross";
import BackButton from "../../Button/BackButton";
import { BottomModal, Button, FlexColumn } from "../../Common/styles.common";
import { getPurchaseOrderStatusConfig, PURCHASE_ORDER_PAGE_CONFIG } from "./config";
import { PO_STATUS } from "../../../../utils/constants/others";
import getHexColor from "utils/getHexColor";
import { CardAction, CardActions, CrossIconWrapper, FilterChip, FiltersRow, Header, HeaderActions, HeaderRow, HeaderTitle, IconButton, MetaItem, MetaLabel, MetaPanel, MetaValue, SearchBox, SearchInput, SearchRow, StateButton, StateButtonCount, StateContainer, StateImage, StateMessage, StateTitle, StatusTag } from "./styles";
import { DesktopPageTabsBar, MobilePageTabsBar } from "components/shared/PageTabs/PageTabs";
import { OrderCard } from "../../../shared/Card/OrderCard";

const formatWeight = (weight) => {
  const numericWeight = Number(weight || 0);
  return `${numericWeight.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    useGrouping: false,
  })} kg`;
};

const formatAmount = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (date) => {
  if (!date || !moment(date).isValid()) {
    return "—";
  }
  return moment(date).format("DD MMM 'YY");
};

export const PurchaseOrderPageTabs = ({
  activePage,
  onChange,
}) => (
  <DesktopPageTabsBar
    tabs={PURCHASE_ORDER_PAGE_CONFIG}
    activeKey={activePage}
    onChange={onChange}
    ariaLabel="Purchase order pages"
  />
);

export const PurchaseOrderMobilePageTabs = ({
  activePage,
  onChange,
}) => (
  <MobilePageTabsBar
    tabs={PURCHASE_ORDER_PAGE_CONFIG}
    activeKey={activePage}
    onChange={onChange}
    ariaLabel="Purchase order pages"
  />
);

export const PurchaseOrderListMobileHeader = ({
  activePage,
  searchQuery,
  searchVisible,
  onPageChange,
  onSearchChange,
  onToggleSearch,
}) => (
  <Header>
    <HeaderRow>
      <BackButton pathName="/" variant="header" />
      <HeaderTitle>
        {searchVisible ? "Search purchase orders" : "Purchase orders (POs)"}
      </HeaderTitle>
      <HeaderActions>
        <IconButton
          type="button"
          aria-label={searchVisible ? "Close search" : "Search purchase orders"}
          onClick={onToggleSearch}
        >
          {searchVisible ? <X size={19} /> : <Search size={19} />}
        </IconButton>
      </HeaderActions>
    </HeaderRow>
    {searchVisible ? (
      <SearchRow>
        <SearchBox>
          <Search size={18} />
          <SearchInput
            autoFocus
            type="search"
            value={searchQuery}
            placeholder="e.g. ZHPHR27-PO-2916760"
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </SearchBox>
      </SearchRow>
    ) : (
      <PurchaseOrderMobilePageTabs
        activePage={activePage}
        onChange={onPageChange}
      />
    )}
  </Header>
);

export const PurchaseOrderStatusFilters = ({
  statuses,
  selectedStatuses,
  onToggle,
}) => {
  if (statuses.length < 2) {
    return null;
  }

  return (
    <FiltersRow aria-label="Filter purchase orders by status">
      {statuses.map((status) => {
        const statusConfig = getPurchaseOrderStatusConfig(status);
        const isSelected = selectedStatuses.includes(status);
        return (
          <FilterChip
            key={status}
            type="button"
            $selected={isSelected}
            aria-pressed={isSelected}
            onClick={() => onToggle(status)}
          >
            <span>{statusConfig.label}</span>
            {isSelected && (
              <CrossIconWrapper
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle(status);
                }}
              >
                <Cross color="#293142" height={16} width={16} />
              </CrossIconWrapper>
            )}
          </FilterChip>
        );
      })}
    </FiltersRow>
  );
};

export const PurchaseOrderStatus = ({ status }) => {
  const statusConfig = getPurchaseOrderStatusConfig(status);
  const colorType = statusConfig.color || "grey";
  const backgroundColor = getHexColor({ type: colorType, tint: 100 });
  const textColor = getHexColor({ type: colorType, tint: 600 });

  return (
    <StatusTag $backgroundColor={backgroundColor} $color={textColor}>
      {statusConfig.label}
    </StatusTag>
  );
};

export const PurchaseOrderCard = ({ item, actions = [], onOpen }) => {
  const po = item;
  const warehouseName = po.warehouse_code
    ? `${po.warehouse_code}${po.warehouse_details?.city ? `, ${po.warehouse_details.city}` : ""}`
    : "—";
  const totalSkus = po.purchase_order_items?.length || 0;
  const totalWeightKg = (po.purchase_order_items || []).reduce(
    (sum, poItem) => sum + (Number(poItem.quantity_ordered || 0) * Number(poItem.weight_per_packet || 0)),
    0
  );
  const weightDisplay = formatWeight(totalWeightKg);
  const metadata = [
    {
      key: "delivery-date",
      icon: CalendarDays,
      label: "Deliver by",
      value: formatDate(po.expected_delivery_date),
    },
    {
      key: "warehouse",
      icon: MapPin,
      label: "Deliver at",
      value: warehouseName,
      wrap: true,
    },
    {
      key: "amount",
      icon: IndianRupee,
      label: "Amount",
      value: formatAmount(po.po_amount),
    },
  ];

  const statusValue =
    po.po_status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL ||
    po.po_status === PO_STATUS.COMPLETED
      ? po.po_status
      : po.derived_status || po.po_status;
  const statusConfig = getPurchaseOrderStatusConfig(statusValue);
  const colorType = statusConfig.color || "grey";
  const headerBgColor = getHexColor({ type: colorType, tint: 100 });
  const headerTextColor = getHexColor({ type: colorType, tint: 600 });

  const rightTopDiv = actions.length > 0 ? (
    <CardActions>
      {actions.map((action) => (
        <CardAction
          key={action.key}
          type="button"
          aria-label={action.label}
          onClick={action.onClick}
          $imageOnly={action.imageOnly}
        >
          {action.icon}
        </CardAction>
      ))}
    </CardActions>
  ) : null;

  const InnerCardContent = (
    <MetaPanel>
      {metadata.map((metadataItem) => {
        const MetadataIcon = metadataItem.icon;
        return (
          <MetaItem key={metadataItem.key} title={`${metadataItem.label}: ${metadataItem.value}`}>
            <MetadataIcon size={17} />
            <MetaLabel>{metadataItem.label}</MetaLabel>
            <MetaValue $wrap={metadataItem.wrap}>{metadataItem.value}</MetaValue>
          </MetaItem>
        );
      })}
    </MetaPanel>
  );

  return (
    <OrderCard
      headerTags={[{ text: statusConfig.label, bg: headerBgColor, text_color: headerTextColor }]}
      headingText={po.purchase_order_number}
      subHeadingText={`${totalSkus} SKUs`}
      rightTopDiv={rightTopDiv}
      InnerCardContent={InnerCardContent}
    />
  );
};

export const PurchaseOrderContactModal = ({ contact, onClose, onCall }) => (
  <BottomModal visible={Boolean(contact)} onClose={onClose} showCloseIcon>
    <FlexColumn className="align-items-center p-4">
      <Cross color="#696969" className="close" onClick={onClose} />
      <img src="/logos/po_creator_logo.svg" alt="" />
      <span className="fs-20 fw-700 mt-4">{contact?.name}</span>
      <span className="fs-14 fw-400 text-gray-cool mt-3 mb-4">PO creator</span>
      <Button type="button" onClick={onCall}>
        <span className="fs-13 fw-500 text-white">Call</span>
      </Button>
    </FlexColumn>
  </BottomModal>
);

export const PurchaseOrderSearchPrompt = ({ minimumCharacters, numericOnly }) => (
  <StateContainer>
    <Search size={42} color="#EF4F5F" />
    <StateTitle>Search by PO number</StateTitle>
    <StateMessage>
      {numericOnly
        ? `Enter the complete ${minimumCharacters}-digit ID shown at the end of the PO number, for example 2916760.`
        : `Enter at least ${minimumCharacters} starting characters of the PO number, for example ZHPHR27-PO-2916760. You can also enter its final 7-digit ID.`}
    </StateMessage>
  </StateContainer>
);

export const PurchaseOrderEmptyState = ({
  pageConfig,
  isSearch,
  selectedStatuses = [],
  nextPage,
  nextPageCount,
  onOpenPage,
}) => {
  const selectedStatusConfig = getPurchaseOrderStatusConfig(selectedStatuses[0]);
  const isFiltered = selectedStatuses.length > 0;
  const title = isSearch
    ? "No matching POs"
    : isFiltered
      ? "No POs here yet"
      : "All caught up!";
  const message = isSearch
    ? "Try a different PO number."
    : isFiltered
      ? selectedStatusConfig.emptyMessage
      : pageConfig.emptyMessage;

  return (
    <StateContainer>
      <StateImage src="/logos/po_empty.svg" alt="" />
      <StateTitle>{title}</StateTitle>
      <StateMessage>{message}</StateMessage>
      {!isSearch && !isFiltered && nextPage && (
        <StateButton type="button" onClick={() => onOpenPage(nextPage.key)}>
          Check {nextPage.label}
          <StateButtonCount>{nextPageCount}</StateButtonCount>
          <ChevronRight size={15} strokeWidth={2.5} />
        </StateButton>
      )}
    </StateContainer>
  );
};

export const PurchaseOrderErrorState = ({ onRetry }) => (
  <StateContainer>
    <StateTitle>Could not load purchase orders</StateTitle>
    <StateMessage>Please check your connection and try again.</StateMessage>
    <StateButton type="button" onClick={onRetry}>
      Retry
    </StateButton>
  </StateContainer>
);
